import { AnyType, ArrayType, BooleanType, DateType, NullType, NumberType, ObjectType, OrType, RecordType, StringType, UndefinedType, UnknownType, } from "./types.js";
export class Schema {
    static clone(type, val) {
        switch (type.def.type) {
            case "any":
            case "unknown":
                throw new Error("Cannot clone any or unknown type", {
                    cause: type,
                });
            case "undefined":
            case "null":
            case "boolean":
            case "string":
            case "number":
            case "Date":
                return val;
            case "array": {
                if (!Schema.isValid(val, type)) {
                    throw new Error("Invalid value for fieldType", {
                        cause: { val, type },
                    });
                }
                const elementType = type.def.element;
                return val.map((value) => {
                    if (!Schema.isValid(value, elementType)) {
                        throw new Error("Invalid value for fieldType", {
                            cause: { val, type },
                        });
                    }
                    return Schema.clone(elementType, value);
                });
            }
            case "record": {
                if (!Schema.isValid(val, type)) {
                    throw new Error("Invalid value for fieldType", {
                        cause: { val, type },
                    });
                }
                const elementType = type.def.element;
                const newValue = {};
                for (const key of Object.keys(val)) {
                    const value = val[key];
                    newValue[key] = this.clone(elementType, value);
                }
                return newValue;
            }
            case "object": {
                if (!Schema.isValid(val, type)) {
                    throw new Error("Invalid value for fieldType", {
                        cause: { val, type },
                    });
                }
                if (val === undefined || val === null)
                    return val;
                const newValue = {};
                for (const key of Object.keys(type.def.shape)) {
                    const value = val[key];
                    const keyType = type.def.shape[key];
                    newValue[key] = this.clone(keyType, value);
                }
                return newValue;
            }
            case "or": {
                for (const subType of type.def.types) {
                    if (Schema.isValid(val, subType)) {
                        return this.clone(subType, val);
                    }
                }
                throw new Error("Invalid value for type", {
                    cause: { val, type },
                });
            }
            default:
                type.def;
        }
        throw new Error("This should be unreachable", {
            cause: type,
        });
    }
    static any() {
        return AnyType;
    }
    static unknown() {
        return UnknownType;
    }
    static undefined() {
        return UndefinedType;
    }
    static null() {
        return NullType;
    }
    static boolean() {
        return BooleanType;
    }
    static number() {
        return NumberType;
    }
    static string() {
        return StringType;
    }
    static date() {
        return DateType;
    }
    static array(elementType) {
        return elementType === undefined ? ArrayType(UnknownType) : ArrayType(elementType);
    }
    static record(element) {
        return element === undefined ? RecordType(UnknownType) : RecordType(element);
    }
    static object(shape) {
        return shape === undefined ? ObjectType({}) : ObjectType(shape);
    }
    static or(types) {
        return OrType(types);
    }
    static defaultValue(schema) {
        if (schema.defaultValue) {
            const defaultValue = schema.defaultValue();
            if (!Schema.isValid(defaultValue, schema)) {
                throw new Error();
            }
            return defaultValue;
        }
        else {
            if (schema._required) {
                throw new Error("No default value, but this is a required type");
            }
            return undefined;
        }
    }
    static keysOf(schema) {
        return Object.keys(schema.def.shape);
    }
    static field(schema, key) {
        return schema.def.shape[key];
    }
    static isValid(val, type) {
        return Schema.validate(val, type).isValid;
    }
    static validate(val, type) {
        const typeName = type.def.type;
        if (val === undefined) {
            return typeName === "undefined" || !type._required
                ? { isValid: true }
                : { isValid: false, reason: "Field is not optional" };
        }
        switch (typeName) {
            case "any":
            case "unknown":
                return { isValid: true };
            case "undefined":
            case "boolean":
            case "string":
            case "number":
                if (typeof val === typeName) {
                    return { isValid: true };
                }
                else {
                    return {
                        isValid: false,
                        reason: `Field must be of type ${typeName}. Found ${typeof val}`,
                    };
                }
            case "null":
                return val === null
                    ? { isValid: true }
                    : {
                        isValid: false,
                        reason: `Field must be null. Found ${typeof val}`,
                    };
            case "Date":
                return val instanceof Date
                    ? { isValid: true }
                    : {
                        isValid: false,
                        reason: `Field must be a Date object. Found ${typeof val}, ${val?.constructor.name}`,
                    };
            case "array": {
                if (!Array.isArray(val)) {
                    return {
                        isValid: false,
                        reason: `Field must be an Array. Found ${typeof val}, ${val?.constructor.name}`,
                    };
                }
                const elementType = type.def.element;
                const failures = {};
                for (let i = 0; i < val.length; i++) {
                    const result = Schema.validate(val[i], elementType);
                    if (!result.isValid) {
                        failures[i] = result.reason;
                    }
                }
                return Object.keys(failures).length === 0
                    ? { isValid: true }
                    : {
                        isValid: false,
                        reason: failures,
                    };
            }
            case "record": {
                if (!val || typeof val !== "object") {
                    return {
                        isValid: false,
                        reason: `Field must be an object. Found ${typeof val}, ${val?.constructor.name}`,
                    };
                }
                const elementType = type.def.element;
                const failures = {};
                for (const key of Object.keys(val)) {
                    const value = val[key];
                    const result = Schema.validate(value, elementType);
                    if (!result.isValid) {
                        failures[key] = result.reason;
                    }
                }
                return Object.keys(failures).length === 0
                    ? { isValid: true }
                    : {
                        isValid: false,
                        reason: failures,
                    };
            }
            case "object": {
                const objectType = type;
                if (!val || typeof val !== "object") {
                    return {
                        isValid: false,
                        reason: `Field must be an object. Found ${typeof val}, ${val?.constructor.name}`,
                    };
                }
                const failures = {};
                for (const key of Object.keys(objectType.def.shape)) {
                    const value = val[key];
                    const result = Schema.validate(value, objectType.def.shape[key]);
                    if (!result.isValid) {
                        failures[key] = result.reason;
                    }
                }
                return Object.keys(failures).length === 0
                    ? { isValid: true }
                    : {
                        isValid: false,
                        reason: failures,
                    };
            }
            case "or": {
                const failures = {};
                for (const subType of type.def.types) {
                    const result = Schema.validate(val, subType);
                    if (result.isValid) {
                        return result;
                    }
                    else {
                        failures[subType.def.type] = result.reason;
                    }
                }
                return {
                    isValid: false,
                    reason: failures,
                };
            }
            default:
                typeName;
        }
        throw new Error("This should be unreachable", {
            cause: type,
        });
    }
    static isKeyOf(key, schema) {
        return typeof key === "string" && key in schema.def.shape;
    }
    static isValidAtKey(val, schema, key) {
        return Schema.isValid(val, schema.def.shape[key]);
    }
}
//# sourceMappingURL=Schema.js.map